import React from 'react';
import { useStyles } from 'components/Statistics/styles';

import { Cell, Pie, PieChart } from 'recharts';
import {
  CHART_WIDTH,
  CHART_HEIGHT,
  PIE_CHART_OUTER_RADIUS,
  INNER_PIE_CHART_OUTER_RADIUS,
  OUTER_PIE_CHART_INNER_RADIUS,
  INNER_PIE_CHART_INNER_RADIUS,
  CHART_COLORS,
  RADIAN,
} from './Statistics';

import Badge from './Badge';
import { bytesToGBOrTB } from './utils';
import EmptyPieChart from './EmptyPieChart';
import { MenuItem, Select } from '@material-ui/core';
import { StatisticsStorageResult } from 'redux/reducers/type';

type StorageProps = {
  statisticsStorageData: PieChartStorageData[];
  totalStorage: PieTotalStorageData;
  statisticsStorage: StatisticsStorageResult[];
  currentStorageIndex: number;
  onChangeCurrentStorage: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function Storage({
  statisticsStorageData,
  totalStorage,
  statisticsStorage,
  currentStorageIndex,
  onChangeCurrentStorage,
}: StorageProps) {
  const classes = useStyles();

  const hasData = statisticsStorageData.some(v => v.space !== 0);

  const used = statisticsStorageData.length > 0 && statisticsStorageData[1].space;
  const percentage = Math.round((used / totalStorage.total) * 100);
  let color = CHART_COLORS.default;
  const isCaution = percentage >= 80 && percentage < 90;
  const isWarning = percentage >= 90;

  if (isCaution) {
    color = CHART_COLORS.caution;
  } else if (isWarning) {
    color = color = CHART_COLORS.warning;
  }

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitleBox}>
        <div className={classes.sectionTitle}>스토리지</div>
        <div>
          <Select value={currentStorageIndex} onChange={onChangeCurrentStorage}>
            {statisticsStorage.length > 0 &&
              statisticsStorage.map((v, i) => (
                <MenuItem key={`statistics-current-storage-${i}`} value={i}>
                  {v.STORAGE_NAME}
                </MenuItem>
              ))}
          </Select>
        </div>
      </div>

      <div className={classes.pieChartWithStorageinfo}>
        <div className={classes.pieChart}>
          {hasData ? (
            <PieChart width={CHART_WIDTH / 2} height={CHART_HEIGHT}>
              <Pie
                data={statisticsStorageData}
                dataKey="space"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={INNER_PIE_CHART_OUTER_RADIUS}
                innerRadius={INNER_PIE_CHART_INNER_RADIUS}
                labelLine={false}
                label={renderCustomizedInnerLabel}
                isAnimationActive={false}
              >
                <Cell fill={CHART_COLORS.disable} />
                <Cell fill={color} />
              </Pie>
              <Pie
                data={statisticsStorageData}
                dataKey="space"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={PIE_CHART_OUTER_RADIUS}
                innerRadius={OUTER_PIE_CHART_INNER_RADIUS}
                label={renderCustomizedOuterLabelSize}
                isAnimationActive={false}
              >
                <Cell fill={CHART_COLORS.disable} />
                <Cell fill={color} />
              </Pie>
            </PieChart>
          ) : (
            <EmptyPieChart />
          )}
          <div
            style={{
              width: INNER_PIE_CHART_INNER_RADIUS * 2,
              height: INNER_PIE_CHART_INNER_RADIUS * 2,
            }}
            className={classes.pieChartCenter}
          >
            {`총 ${bytesToGBOrTB(totalStorage.total)}`}
          </div>
        </div>

        {
          <div className={classes.storageInfo}>
            <div className={classes.storageName}>
              {hasData ? statisticsStorage[currentStorageIndex].STORAGE_NAME : ''}
              {(isCaution || isWarning) && (
                <div style={{ backgroundColor: color }} className={classes.statusDot}></div>
              )}
            </div>
            <div className={classes.storageDetail}>
              <span>총 용량</span>
              <span className={classes.storageDetailData}>
                {hasData
                  ? bytesToGBOrTB(statisticsStorage[currentStorageIndex].TOTAL_SPACE)
                  : '0 GB'}
              </span>
            </div>
            <div className={classes.storageDetail}>
              <span>사용 가능 용량</span>
              <span className={classes.storageDetailData}>
                {hasData
                  ? bytesToGBOrTB(statisticsStorage[currentStorageIndex].USABLE_SPACE)
                  : '0 GB'}
              </span>
            </div>
            <div className={classes.storageDetail}>
              <span>사용중인 용량</span>
              <span className={classes.storageDetailData}>
                {hasData
                  ? bytesToGBOrTB(statisticsStorage[currentStorageIndex].USED_SPACE)
                  : '0 GB'}
              </span>
            </div>
            {(isCaution || isWarning) && (
              <div style={{ color }}>* 스토리지 용량을 확인해주세요.</div>
            )}
          </div>
        }
      </div>
    </div>
  );
}

function renderCustomizedInnerLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
}) {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5 - 5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return value === 0 ? null : (
    <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function renderCustomizedOuterLabelSize({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  name,
  value,
}) {
  const radius = innerRadius + (outerRadius - innerRadius) + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <>
      <text
        x={x}
        y={y - 8}
        fontSize={12}
        fontWeight={900}
        fill="black"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {name}
      </text>
      <text
        x={x}
        y={y + 8}
        fontSize={10}
        fill="grey"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${bytesToGBOrTB(value)}`}
      </text>
    </>
  );
}

export default Storage;
